import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Plus, Trash2, GripVertical, Save, X, ChevronDown, ToggleLeft, ListOrdered, TextCursorInput, AlignLeft, AlignJustify, FileText, Users, User, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: "short_text", label: "Short Text", icon: AlignLeft },
  { value: "long_text", label: "Long Text", icon: AlignJustify },
  { value: "true_false", label: "True / False", icon: ToggleLeft },
  { value: "ranking", label: "Ranking / Scale", icon: ListOrdered },
  { value: "fill_blank", label: "Fill in the Blank", icon: TextCursorInput },
  { value: "instruction", label: "Instruction / Info", icon: Info },
];

const QuestionTypeSelector = ({ questionType, onChange, compact = false }) => {
  const [open, setOpen] = useState(false);
  const current = QUESTION_TYPES.find(t => t.value === questionType) || QUESTION_TYPES[1];
  const Icon = current.icon;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-white/30 transition-all ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'}`}
      >
        <Icon className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-[#E91E8C]`} />
        <span className="text-[#E2D9F3]">{current.label}</span>
        <ChevronDown className={`w-3 h-3 text-[#6B5E7C] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 rounded-xl bg-[#1a0a2e] border border-white/10 shadow-xl z-50 py-1">
          {QUESTION_TYPES.map(type => {
            const TypeIcon = type.icon;
            return (
              <button
                key={type.value}
                type="button"
                onClick={() => { onChange(type.value); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-white/10 ${
                  questionType === type.value ? 'bg-[#E91E8C]/15' : ''
                }`}
              >
                <TypeIcon className={`w-4 h-4 ${questionType === type.value ? 'text-[#E91E8C]' : 'text-[#6B5E7C]'}`} />
                <span className="text-sm text-white">{type.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TypeConfig = ({ item, onChange }) => {
  const qType = item.question_type || "long_text";

  if (qType === "ranking") {
    const items = item.ranking_items || [];
    const isScale = items.length === 0;
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center gap-3 text-xs text-[#6B5E7C]">
          <button type="button" onClick={() => onChange({ ranking_items: [], scale_min: item.scale_min || 1, scale_max: item.scale_max || 5 })}
            className={`px-2 py-1 rounded ${isScale ? 'bg-[#E91E8C]/20 text-[#E91E8C]' : 'hover:bg-white/10'}`}>Scale</button>
          <button type="button" onClick={() => onChange({ ranking_items: items.length ? items : ["Item 1", "Item 2", "Item 3"] })}
            className={`px-2 py-1 rounded ${!isScale ? 'bg-[#E91E8C]/20 text-[#E91E8C]' : 'hover:bg-white/10'}`}>Rank Items</button>
        </div>
        {isScale ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B5E7C]">Scale:</span>
            <Input type="number" value={item.scale_min || 1} onChange={(e) => onChange({ scale_min: parseInt(e.target.value) || 1 })} className="input-dark w-14 text-center text-sm" />
            <span className="text-[#6B5E7C]">to</span>
            <Input type="number" value={item.scale_max || 5} onChange={(e) => onChange({ scale_max: parseInt(e.target.value) || 5 })} className="input-dark w-14 text-center text-sm" />
          </div>
        ) : (
          <div className="space-y-1">
            {items.map((itm, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-[#6B5E7C] w-4">{i + 1}.</span>
                <Input value={itm} onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange({ ranking_items: n }); }}
                  className="input-dark text-sm flex-1" placeholder={`Item ${i + 1}`} />
                <button type="button" onClick={() => onChange({ ranking_items: items.filter((_, j) => j !== i) })} className="text-[#FF1744] hover:text-white p-1"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            <Button type="button" onClick={() => onChange({ ranking_items: [...items, `Item ${items.length + 1}`] })} variant="ghost" size="sm" className="text-[#E91E8C] text-xs">
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (qType === "fill_blank") {
    return (
      <div className="mt-2">
        <Input value={item.blank_template || ""} onChange={(e) => onChange({ blank_template: e.target.value })}
          className="input-dark text-sm" placeholder="The most important skill is ___ because..." />
        <p className="text-xs text-[#6B5E7C] mt-1">Use ___ where the blank goes</p>
      </div>
    );
  }
  return null;
};

// A single part (sub-question) editor row
const PartEditor = ({ part, index, label, onUpdate, onRemove }) => (
  <div className="ml-6 flex items-start gap-2 group border-l-2 border-white/10 pl-3 py-1">
    <span className="text-[#E91E8C] font-bold text-sm mt-2 w-4">{label})</span>
    <div className="flex-1 space-y-1">
      <div className="flex items-center gap-2">
        <Input value={part.text} onChange={(e) => onUpdate(index, { text: e.target.value })}
          className="input-dark text-sm flex-1" placeholder={part.question_type === "instruction" ? "Instruction text (no answer needed)" : "Sub-question text..."} />
        <button type="button" onClick={() => onRemove(index)}
          className="text-[#FF1744] hover:text-white p-1 opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
      </div>
      <QuestionTypeSelector compact questionType={part.question_type || "long_text"} onChange={(t) => onUpdate(index, { question_type: t })} />
      <TypeConfig item={part} onChange={(fields) => onUpdate(index, fields)} />
    </div>
  </div>
);

const ScopeToggle = ({ scope, onChange }) => (
  <div className="flex items-center rounded-lg bg-white/5 border border-white/10 overflow-hidden">
    <button type="button"
      onClick={() => onChange("individual")}
      className={`flex items-center gap-1 px-2 py-1 text-[10px] transition-all ${
        scope !== "team" ? 'bg-[#E91E8C]/20 text-[#E91E8C]' : 'text-[#6B5E7C] hover:text-white'
      }`}>
      <User className="w-3 h-3" /> Individual
    </button>
    <button type="button"
      onClick={() => onChange("team")}
      className={`flex items-center gap-1 px-2 py-1 text-[10px] transition-all ${
        scope === "team" ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'text-[#6B5E7C] hover:text-white'
      }`}>
      <Users className="w-3 h-3" /> Team Lead
    </button>
  </div>
);

const StationEditor = ({ station, sessionId, onSave, onCancel }) => {
  const [name, setName] = useState(station.name);
  const [instructions, setInstructions] = useState(station.instructions || "");
  const [showInstructions, setShowInstructions] = useState(!!(station.instructions?.trim()));
  const [hideQuestionsOnScreen, setHideQuestionsOnScreen] = useState(!!station.hide_questions_on_screen);
  const [questions, setQuestions] = useState(
    station.questions?.map((q) => ({
      ...q,
      question_type: q.question_type || "long_text",
      ranking_items: q.ranking_items || [],
      blank_template: q.blank_template || "",
      scale_min: q.scale_min ?? 1,
      scale_max: q.scale_max ?? 5,
      scope: q.scope || "individual",
      parts: (q.parts || []).map(p => ({
        ...p,
        question_type: p.question_type || "long_text",
        ranking_items: p.ranking_items || [],
        blank_template: p.blank_template || "",
        scale_min: p.scale_min ?? 1,
        scale_max: p.scale_max ?? 5,
        scope: p.scope || q.scope || "individual",
      })),
    })) || []
  );
  const [isSaving, setIsSaving] = useState(false);

  const addQuestion = () => {
    setQuestions([...questions, {
      id: `new_${Date.now()}`, text: "", order: questions.length,
      question_type: "long_text", ranking_items: [], blank_template: "", scale_min: 1, scale_max: 5, parts: [],
      scope: "individual",
    }]);
  };

  const removeQuestion = (index) => setQuestions(questions.filter((_, i) => i !== index));

  const updateQuestion = (index, fields) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...fields };
    setQuestions(updated);
  };

  const addPart = (qIndex) => {
    const updated = [...questions];
    const parts = updated[qIndex].parts || [];
    parts.push({
      id: `part_${Date.now()}`, label: chr(parts.length), text: "",
      question_type: "long_text", ranking_items: [], blank_template: "", scale_min: 1, scale_max: 5,
    });
    updated[qIndex] = { ...updated[qIndex], parts };
    setQuestions(updated);
  };

  const updatePart = (qIndex, pIndex, fields) => {
    const updated = [...questions];
    const parts = [...(updated[qIndex].parts || [])];
    parts[pIndex] = { ...parts[pIndex], ...fields };
    updated[qIndex] = { ...updated[qIndex], parts };
    setQuestions(updated);
  };

  const removePart = (qIndex, pIndex) => {
    const updated = [...questions];
    const parts = (updated[qIndex].parts || []).filter((_, i) => i !== pIndex);
    updated[qIndex] = { ...updated[qIndex], parts };
    setQuestions(updated);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Station name is required"); return; }
    setIsSaving(true);
    try {
      const payload = {
        name: name.trim(),
        content_type: "questions",
        instructions: instructions.trim(),
        hide_questions_on_screen: hideQuestionsOnScreen,
        questions: questions.filter(q => q.text.trim()).map((q, idx) => ({
          id: q.id, text: q.text.trim(), order: idx,
          question_type: q.question_type || "long_text",
          ranking_items: q.ranking_items || [],
          blank_template: q.blank_template || "",
          scale_min: q.scale_min ?? 1,
          scale_max: q.scale_max ?? 5,
          scope: q.scope || "individual",
          parts: (q.parts || []).filter(p => p.text.trim()).map((p, pidx) => ({
            id: p.id, label: chr(pidx), text: p.text.trim(),
            question_type: p.question_type || "long_text",
            ranking_items: p.ranking_items || [],
            blank_template: p.blank_template || "",
            scale_min: p.scale_min ?? 1,
            scale_max: p.scale_max ?? 5,
            scope: p.scope || q.scope || "individual",
          })),
        })),
      };
      await axios.put(`${API}/sessions/${sessionId}/stations/${station.id}`, payload);
      toast.success("Station updated!");
      onSave();
    } catch (error) {
      console.error("Error saving station:", error);
      toast.error("Failed to save station");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Station Name */}
      <div>
        <label className="block text-sm font-medium text-[#E2D9F3] mb-2">Station Name</label>
        <Input data-testid="station-name-input" value={name} onChange={(e) => setName(e.target.value)}
          className="input-dark" placeholder="e.g., Welcome Station" />
      </div>

      {/* Instructions (optional, shown first on screen) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-[#E2D9F3] flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Instructions / Intro Text
          </label>
          <button type="button" onClick={() => setShowInstructions(!showInstructions)}
            className={`text-xs px-3 py-1 rounded-full transition-all ${
              showInstructions ? 'bg-[#E91E8C]/20 text-[#E91E8C]' : 'bg-white/5 text-[#6B5E7C] hover:text-white'
            }`}>
            {showInstructions ? 'Enabled' : 'Add Instructions'}
          </button>
        </div>
        {showInstructions && (
          <div>
            <Textarea data-testid="station-instructions-input" value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="input-dark min-h-[100px]"
              placeholder="Instructions shown on screen before questions begin..." />
            <p className="text-xs text-[#6B5E7C] mt-1">Displayed on screen first. Participants tap "Continue" to move to questions.</p>
            <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideQuestionsOnScreen}
                onChange={(e) => setHideQuestionsOnScreen(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#E91E8C]"
                data-testid="hide-questions-toggle"
              />
              <span className="text-xs text-[#E2D9F3]">
                Show <strong>only the instructions</strong> on the projector station screen (hide the questions below).
                <span className="block text-[#6B5E7C]">Participants still answer the questions on their own devices.</span>
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Question Sets */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-[#E2D9F3]">Question Sets</label>
          <Button data-testid="add-question-btn" type="button" onClick={addQuestion}
            variant="ghost" size="sm" className="text-[#E91E8C] hover:text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Question
          </Button>
        </div>

        <div className="space-y-4">
          {questions.map((question, qIdx) => {
            const hasParts = question.parts && question.parts.length > 0;
            return (
              <div key={question.id} className="glass-light rounded-xl p-4 relative" data-testid={`question-${qIdx}`} style={{ zIndex: questions.length - qIdx }}>
                {/* Question header */}
                <div className="flex items-start gap-3 group">
                  <GripVertical className="w-4 h-4 text-[#6B5E7C] mt-3 opacity-50" />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[#E91E8C] font-bold">{qIdx + 1}.</span>
                      <Input data-testid={`question-input-${qIdx}`} value={question.text}
                        onChange={(e) => updateQuestion(qIdx, { text: e.target.value })}
                        className="input-dark flex-1"
                        placeholder={hasParts ? "Question set title (e.g., Team Collaboration)" : (question.question_type === "instruction" ? "Instruction / info text (no answer needed)" : "Enter your question...")} />
                      <Button type="button" onClick={() => removeQuestion(qIdx)} variant="ghost" size="sm"
                        className="text-[#FF1744] hover:text-white hover:bg-[#FF1744]/20 opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* If no parts, show type selector and scope for the question itself */}
                    {!hasParts && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <QuestionTypeSelector questionType={question.question_type || "long_text"}
                          onChange={(t) => updateQuestion(qIdx, { question_type: t })} />
                        {question.question_type !== "instruction" && (
                          <ScopeToggle scope={question.scope || "individual"}
                            onChange={(s) => updateQuestion(qIdx, { scope: s })} />
                        )}
                      </div>
                    )}
                    {!hasParts && <TypeConfig item={question} onChange={(fields) => updateQuestion(qIdx, fields)} />}

                    {/* If has parts, show scope toggle at the set level */}
                    {hasParts && (
                      <div className="flex items-center gap-2 mt-1">
                        <ScopeToggle scope={question.scope || "individual"}
                          onChange={(s) => updateQuestion(qIdx, { scope: s })} />
                        <span className="text-[10px] text-[#6B5E7C]">applies to all parts</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sub-questions (parts a, b, c...) */}
                {hasParts && (
                  <div className="mt-3 space-y-2">
                    {question.parts.map((part, pIdx) => (
                      <PartEditor key={part.id} part={part} index={pIdx} label={chr(pIdx)}
                        onUpdate={(i, fields) => updatePart(qIdx, i, fields)}
                        onRemove={(i) => removePart(qIdx, i)} />
                    ))}
                  </div>
                )}

                {/* Add part button */}
                <div className="mt-2 ml-6">
                  <Button type="button" onClick={() => addPart(qIdx)} variant="ghost" size="sm"
                    className="text-[#8B5CF6] text-xs hover:text-white">
                    <Plus className="w-3 h-3 mr-1" />
                    {hasParts ? `Add part ${chr(question.parts.length)}` : "Add sub-questions (a, b, c...)"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {questions.length === 0 && (
          <div className="text-center py-6 text-[#6B5E7C] bg-white/5 rounded-xl">
            <p>No questions yet. Click "Add Question" to get started.</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
        <Button data-testid="cancel-edit-btn" type="button" onClick={onCancel} variant="ghost" className="btn-ghost">
          <X className="w-4 h-4 mr-2" /> Cancel
        </Button>
        <Button data-testid="save-station-btn" type="button" onClick={handleSave} disabled={isSaving}
          className="btn-primary-gradient control-btn">
          <Save className="w-4 h-4 mr-2" /> {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

// Helper: convert index to letter (0->a, 1->b, etc.)
function chr(i) {
  return String.fromCharCode(97 + i);
}

export default StationEditor;
